const METRIC_KEYS = [
  "att_unique_users",
  "att_rx_data",
  "att_tx_data",
  "tmo_unique_users",
  "tmo_rx_data",
  "tmo_tx_data",
];

const toNumber = (value) => {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
};

const sortRows = (rows) => [...rows].sort((first, second) => {
  const dateOrder = first.est_day.localeCompare(second.est_day);
  return dateOrder || first.building.localeCompare(second.building);
});

export const normalizeBuildingRows = (rows) => sortRows(
  (Array.isArray(rows) ? rows : [])
    .filter((row) => row?.est_day && row?.building)
    .map((row) => ({
      est_day: String(row.est_day).slice(0, 10),
      building: String(row.building),
      ...Object.fromEntries(METRIC_KEYS.map((key) => [key, toNumber(row[key])])),
      last_updated: row.last_updated || null,
    }))
);

export const getAvailableBuildings = (payload, rows) => {
  const configured = Array.isArray(payload?.available_buildings)
    ? payload.available_buildings
    : [];
  const values = configured.length ? configured : rows.map((row) => row.building);
  return [...new Set(values.map(String))].sort((first, second) => first.localeCompare(second));
};

export const buildDailyBuildingData = (rows) => {
  const byDate = new Map();

  normalizeBuildingRows(rows).forEach((row) => {
    if (!byDate.has(row.est_day)) {
      byDate.set(row.est_day, {
        est_day: row.est_day,
        buildings: new Set(),
        ...Object.fromEntries(METRIC_KEYS.map((key) => [key, 0])),
      });
    }

    const day = byDate.get(row.est_day);
    day.buildings.add(row.building);
    METRIC_KEYS.forEach((key) => {
      day[key] += row[key];
    });
  });

  return [...byDate.values()].map(({ buildings, ...day }) => ({
    ...day,
    building_count: buildings.size,
    total_unique_users: day.att_unique_users + day.tmo_unique_users,
    total_rx_data: day.att_rx_data + day.tmo_rx_data,
    total_tx_data: day.att_tx_data + day.tmo_tx_data,
  }));
};

const summarizeDailyRows = (dailyRows) => {
  const dayCount = dailyRows.length;
  const totals = Object.fromEntries(METRIC_KEYS.map((key) => [key, 0]));

  dailyRows.forEach((row) => {
    METRIC_KEYS.forEach((key) => {
      totals[key] += row[key];
    });
  });

  return {
    day_count: dayCount,
    att_average_users: dayCount ? totals.att_unique_users / dayCount : 0,
    tmo_average_users: dayCount ? totals.tmo_unique_users / dayCount : 0,
    total_average_users: dayCount
      ? (totals.att_unique_users + totals.tmo_unique_users) / dayCount
      : 0,
    att_rx_data: totals.att_rx_data,
    att_tx_data: totals.att_tx_data,
    tmo_rx_data: totals.tmo_rx_data,
    tmo_tx_data: totals.tmo_tx_data,
    total_rx_data: totals.att_rx_data + totals.tmo_rx_data,
    total_tx_data: totals.att_tx_data + totals.tmo_tx_data,
  };
};

export const buildOverallBuildingSummary = (rows) => {
  const normalizedRows = normalizeBuildingRows(rows);
  return {
    building_count: new Set(normalizedRows.map((row) => row.building)).size,
    ...summarizeDailyRows(buildDailyBuildingData(normalizedRows)),
  };
};

export const buildBuildingSummaries = (rows) => {
  const byBuilding = new Map();

  normalizeBuildingRows(rows).forEach((row) => {
    if (!byBuilding.has(row.building)) byBuilding.set(row.building, []);
    byBuilding.get(row.building).push(row);
  });

  return [...byBuilding.entries()]
    .map(([building, buildingRows]) => ({
      building,
      ...summarizeDailyRows(buildingRows),
    }))
    .sort((first, second) => first.building.localeCompare(second.building));
};

const escapeCsvCell = (value) => {
  const text = value == null ? "" : String(value);
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export const buildBuildingCsv = (ranges) => {
  const header = [
    "Range",
    "Date",
    "Building",
    "AT&T Unique Users",
    "AT&T RX Bytes",
    "AT&T TX Bytes",
    "T-Mobile Unique Users",
    "T-Mobile RX Bytes",
    "T-Mobile TX Bytes",
    "Last Updated",
  ];
  const dataRows = ranges.flatMap(({ label, rows }) => normalizeBuildingRows(rows).map((row) => [
    label,
    row.est_day,
    row.building,
    row.att_unique_users,
    row.att_rx_data,
    row.att_tx_data,
    row.tmo_unique_users,
    row.tmo_rx_data,
    row.tmo_tx_data,
    row.last_updated,
  ]));

  return [header, ...dataRows]
    .map((row) => row.map(escapeCsvCell).join(","))
    .join("\n");
};
